/* eslint-disable react/no-unknown-property */
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, Lock, ArrowRight, GoogleLogo } from "@phosphor-icons/react";

/**
 * Referrer-only sign-in page. Shares the same /auth/login backend endpoint
 * as solicitors/clients but enforces role == "referrer" client-side so a
 * solicitor signing in here sees a clear error and is bounced to /login.
 *
 * Two paths:
 *   1. Email + password (issued by solicitor invite)
 *   2. Google sign-in via Emergent (one-click)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function ReferrerLoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Already authenticated — bounce by role
  if (user) {
    if (user.role === "referrer" && user.referrer_token) {
      return <Navigate to={`/referrer-dashboard/${user.referrer_token}`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u.role !== "referrer") {
        setError(
          "This sign-in is for referrers only. Use the firm sign-in instead."
        );
        // Wipe the just-issued JWT so the wrong-role user isn't auto-logged-in
        try { await API.post("/auth/logout"); } catch { /* noop */ }
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        return;
      }
      navigate(`/referrer-dashboard/${u.referrer_token || ""}`);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Sign in failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="referrer-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8 p-4 rounded-lg" style={{ background: "#100f5d" }}>
          <img src="/logo.png" alt="UKC Legal Solicitors" className="h-10 w-auto" />
        </div>

        <h1
          className="text-2xl sm:text-3xl font-bold mb-2"
          style={{ fontFamily: "Outfit, sans-serif", color: "#100f5d" }}
        >
          Referrer sign-in
        </h1>
        <p className="text-slate-500 mb-8 text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
          Track the cases you&rsquo;ve referred and your fee statements.
        </p>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="referrer-auth-error">
            {error}
          </div>
        )}

        <Button
          type="button"
          onClick={handleGoogle}
          data-testid="referrer-google-btn"
          variant="outline"
          className="w-full h-11 mb-4 font-medium border-slate-300 hover:bg-slate-50"
        >
          <GoogleLogo weight="bold" className="w-4 h-4 mr-2 text-[#4285F4]" />
          Continue with Google
        </Button>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
            <span className="bg-slate-50 px-3 text-slate-400" style={{ fontFamily: "Manrope, sans-serif" }}>
              or with password
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="referrer-auth-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                data-testid="referrer-email-input"
                placeholder="you@agency.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-11"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type="password"
                data-testid="referrer-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-11"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="referrer-submit-btn"
            disabled={submitting}
            className="w-full h-11 text-white font-medium rounded-lg"
            style={{ background: "#100f5d" }}
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <span className="flex items-center justify-center gap-2">
                Sign in <ArrowRight weight="bold" className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>

        <div className="mt-6 space-y-2 text-center text-xs text-slate-500" style={{ fontFamily: "Manrope, sans-serif" }}>
          <p>
            First-time? <Link to="/referrer-setup" className="font-semibold hover:underline" style={{ color: "#100f5d" }} data-testid="setup-pwd-link">Set up a password</Link> using the link your solicitor sent you.
          </p>
          <p className="text-slate-400">
            Firm member? <Link to="/login" className="hover:underline" data-testid="firm-login-link">Sign in here</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
